import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FormGroup, FormText, Label } from "reactstrap";
import { Button } from "../../../Helper/Button";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useTenantFacilityLocations } from "../../../../hooks/useFacilityLocationQueries";
import MetaForm from "../../../Forms/MetaForm";
import RoomLinksModal from "../RoomLinksModal";
import { yesOrNoRadio } from "./util";

export { defaultRipewiseConfig } from "../../../../actions/Tenants/config/ripewise";

// * Which EYE ripening room is which room on the gateway's controller; same popup as under facility locations.
function RoomLinks() {
    const { tenant_id: tenantId = "" } = useParams();
    const [open, setOpen] = useState(false);
    const { data, refetch } = useTenantFacilityLocations(tenantId, { offset: 0, limit: 1000, type: "ripening_room" });
    const rooms = data?.results ?? [];
    const linked = rooms.filter((room) => !!room.gateway && !!room.controller_room).length;

    return <FormGroup>
        <Label className="d-block">Room links</Label>
        <div className="d-flex align-items-center gap-3">
            <span><b>{linked}</b> of <b>{rooms.length}</b> ripening rooms linked to a gateway room.</span>
            <Button color="primary" outline size="sm" icon={faLink} disabled={!tenantId} onClick={() => setOpen(true)}>Room links</Button>
        </div>
        <FormText className="d-block">Links each ripening room to its room on the gateway&apos;s controller. Also under <Link to={`/tenants/${tenantId}/facility-locations`}>facility locations</Link>.</FormText>
        <RoomLinksModal tenantId={tenantId} isOpen={open} toggle={() => { setOpen(false); refetch(); }} />
    </FormGroup>;
}

export default function RipewiseConfigForm({ config_data, setConfigData }) {
    const config = useConfig();

    const form = [
        {
            label: "Enable RipeWise",
            name: "enabled",
            description: "Master switch. When off, EYE makes no RipeWise calls for this tenant: no room status, no programs and no sensor data from RipeWise. Defaults to off.",
            ...yesOrNoRadio,
        },
        {
            type: "text",
            name: "tenant_id",
            label: "RipeWise tenant ID",
            placeholder: "Tenant ID used by RipeWise",
            description: "RipeWise (the ripening controller) sometimes stores this tenant's gateways under a different tenant ID than EYE uses. Set that RipeWise tenant ID here if ripening rooms show no sensor or controller data. Leave empty if EYE and RipeWise use the same ID for this tenant.",
        },
        {
            label: "Allow control",
            name: "control",
            description: "When on, EYE may control the ripening rooms through RipeWise: ripening programs, starting and stopping, and editing a running program. When off, EYE only reads (room status, sensor data). Defaults to off.",
            ...yesOrNoRadio,
        },
        {
            type: "radio",
            name: "sensor_data",
            label: "Sensor data",
            description: "Where the ripening room graph gets its sensor data. Defaults to EYE.",
            options: [
                { value: "eye", label: "EYE only" },
                { value: "ripewise", label: "RipeWise only" },
                { value: "eye_first", label: "EYE, RipeWise when EYE has none" },
                { value: "ripewise_first", label: "RipeWise, EYE when RipeWise has none" },
            ],
        },
        {
            label: "Temperature limits per fruit",
            name: "fruit_limits",
            type: "object-list",
            description: "Set in every ripening program of the fruit and shown read-only in the program form. Leave a value empty to let the program set it.",
            getItemTitle: (item) => `${item.fruit_type || "—"}: air ${item.T_Air_Min ?? "—"} to ${item.T_Air_Max ?? "—"} °C, product ${item.T_Prod_Min ?? "—"} to ${item.T_Prod_Max ?? "—"} °C`,
            fields: [
                {
                    label: "Fruit type",
                    name: "fruit_type",
                    type: "single-select",
                    options: config.fruit_types.map(({ value, text }) => ({ value, label: text || value })),
                },
                { label: "Air temperature min (°C)", name: "T_Air_Min", type: "number" },
                { label: "Air temperature max (°C)", name: "T_Air_Max", type: "number" },
                { label: "Product temperature min (°C)", name: "T_Prod_Min", type: "number" },
                { label: "Product temperature max (°C)", name: "T_Prod_Max", type: "number" },
            ],
        },
    ];

    return <>
        <MetaForm meta={form} setValue={(field, value) => setConfigData({ ...config_data, [field]: value })} object={config_data} config={config} />
        <RoomLinks />
    </>;
}
